import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, Phone, Calendar, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInquiries = () => {
  const { getAuthHeader } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`, {
        headers: getAuthHeader()
      });
      setInquiries(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(
        `${API}/inquiries/${id}`,
        null,
        {
          params: { status },
          headers: getAuthHeader()
        }
      );
      toast.success('Inquiry status updated!');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredInquiries = filter === 'all' 
    ? inquiries 
    : inquiries.filter(i => i.status === filter);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-inquiries-page">
      <div className="mb-8">
        <h1 className="text-4xl font-playfair font-bold text-stone-900 mb-2">Inquiries</h1>
        <p className="text-stone-600">Manage customer inquiries</p>
      </div>

      <div className="flex gap-3 mb-6" data-testid="inquiry-filters">
        {['all', 'new', 'contacted', 'closed'].map((status) => (
          <Button
            key={status}
            onClick={() => setFilter(status)}
            data-testid={`filter-${status}`}
            variant={filter === status ? 'default' : 'outline'}
            className={`px-6 py-2 uppercase tracking-widest text-xs font-bold ${
              filter === status
                ? 'bg-emerald-900 text-white'
                : 'border-stone-300 text-stone-700'
            }`}
          >
            {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>

      <div className="space-y-4" data-testid="inquiries-list">
        {filteredInquiries.map((inquiry) => (
          <div
            key={inquiry.id}
            data-testid={`inquiry-item-${inquiry.id}`}
            className="bg-white border border-stone-200 p-6 hover:border-emerald-900 transition-colors"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-playfair font-bold text-stone-900 mb-2">{inquiry.name}</h3>
                <div className="flex flex-wrap gap-4 text-sm text-stone-600">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {inquiry.email}
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {inquiry.phone}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {formatDate(inquiry.created_at)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={inquiry.status}
                  onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                  data-testid={`status-select-${inquiry.id}`}
                  className={`px-3 py-1 text-xs uppercase tracking-widest font-bold border rounded ${
                    inquiry.status === 'new'
                      ? 'bg-amber-100 text-amber-900 border-amber-300'
                      : inquiry.status === 'contacted'
                      ? 'bg-blue-100 text-blue-900 border-blue-300'
                      : 'bg-green-100 text-green-900 border-green-300'
                  }`}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {inquiry.property_interest && (
              <div className="mb-3 text-sm">
                <span className="font-bold text-stone-900">Property Interest: </span>
                <span className="text-stone-600">{inquiry.property_interest}</span>
              </div>
            )}

            <div className="p-4 bg-stone-50 border border-stone-200">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-stone-600" />
                <span className="font-bold text-stone-900 text-sm">Message:</span>
              </div>
              <p className="text-stone-700 leading-relaxed">{inquiry.message}</p>
            </div>
          </div>
        ))}
      </div>

      {filteredInquiries.length === 0 && (
        <div className="text-center py-20 bg-white border border-stone-200">
          <p className="text-stone-500">No inquiries found.</p>
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;